import type { CustomerOrderDetail } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { orderDepositPath, paymentCheckoutPath } from '@/shared/config';
import { Button, buttonVariants, cn } from '@/shared/ui';

interface OrderActionsProps {
  readonly order: CustomerOrderDetail;
  readonly onCancelUnpaid: () => void;
  readonly onRequestCancel: () => void;
  /** 견적서 인쇄 — 상세 위젯이 브라우저 인쇄창을 연다(인쇄 모양은 QuoteDocument). */
  readonly onPrintQuote: () => void;
  readonly pending?: boolean;
}

/** 보조 버튼 — 내 견적 상세 버튼 줄처럼 같은 폭으로 나눠 선다. */
const SECONDARY_CLASS = 'h-12 min-w-28 flex-1 border-foreground/20 text-sm';
/** 되돌릴 수 없는 버튼 — 빨간 면 대신 빨간 글자·테두리로 덜 소란하게 둔다(내 견적 상세와 같다). */
const DESTRUCTIVE_OUTLINE_CLASS =
  'border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive';

/**
 * 주문 처리 버튼(주문 설계 §8-2) — 내 견적 상세와 같은 배치: 위 큰 주 버튼(결제 대기면 결제하기), 아래 같은 폭 보조 버튼 줄.
 * 결제 전 취소는 결제 대기에서만, 취소 요청은 결제 완료·착수 전·처리 중 요청 없음일 때만, 견적서 인쇄는 언제나 둔다.
 * 배송지 변경은 배송지 카드마다 둔다(SUPER-86 spec §5-4).
 */
export const OrderActions = ({
  order,
  onCancelUnpaid,
  onRequestCancel,
  onPrintQuote,
  pending = false,
}: OrderActionsProps): ReactElement => {
  const unpaid = order.status === 'PENDING_PAYMENT';
  const editable = order.status === 'PAID' && !order.pendingCancelRequest;
  const canRequestCancel = editable && order.fulfillmentStatus === 'WAITING';

  return (
    <nav aria-label="주문 처리" className="flex flex-col gap-3 border-t border-foreground/10 pt-6">
      {unpaid ? (
        <Link
          to={
            order.bankTransfer?.status === 'READY'
              ? orderDepositPath(order.id)
              : paymentCheckoutPath(order.id)
          }
          className={cn(buttonVariants({ size: 'lg' }), 'h-14 w-full text-base')}
        >
          {order.bankTransfer?.status === 'READY' ? '입금 안내 보기' : '결제하기'}
        </Link>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {unpaid ? (
          <Button
            type="button"
            variant="outline"
            className={cn(SECONDARY_CLASS, DESTRUCTIVE_OUTLINE_CLASS)}
            disabled={pending}
            onClick={onCancelUnpaid}
          >
            주문 취소
          </Button>
        ) : null}
        {canRequestCancel ? (
          <Button
            type="button"
            variant="outline"
            className={cn(SECONDARY_CLASS, DESTRUCTIVE_OUTLINE_CLASS)}
            disabled={pending}
            onClick={onRequestCancel}
          >
            취소 요청
          </Button>
        ) : null}
        <Button
          type="button"
          variant="outline"
          className={SECONDARY_CLASS}
          disabled={pending}
          onClick={onPrintQuote}
        >
          견적서 인쇄
        </Button>
      </div>
    </nav>
  );
};
